import { BankAccountDataModel, BankAccountRepository, SepaModel } from '@/Contract/domain'
import { BankAccountChangeForm, BankAccountConfirmationForm } from '@/Contract/ui/hooks'
import { LogaltyModel, LogaltyResponseStatus } from '@/Logalty/domain'
import { HttpMethod, HttpModel, MetaModel } from '@/Shared/domain'
import { axiosFetch, createQueryString } from '@/Shared/infrastructure'
import {
  acceptSepaDocument,
  getBankAccountData,
  getSepaDocument,
  getSignStatus,
} from '@/Shared/infrastructure/endpoints'

export function createApiBankAccountRepository(): BankAccountRepository {
  return new ApiBankAccountRepository()
}

type ApiAllowedPhonesForChange = {
  id: number
  maskedPhoneNumber: string
}

type ApiBankAccountDataModel = {
  merchantId: number
  merchantParentId: number | null
  merchantParentName: string | null
  hasMerchantHardDependency: boolean
  merchantMasterId: number | null
  merchantMasterAlias: string | null
  merchantSuperMasterAlias: string | null
  merchantName: string
  merchantCIF: string
  bankAccountId: number
  bankAccountEntity: number
  bankAccountAgency: string
  bankAccountNumber: string
  bankAccountCheckKey: string
  bankAccountIBAN: string
  allowedPhonesForChange: ApiAllowedPhonesForChange[]
}

const BankDataFromApiToDomain = (
  apiData: ApiBankAccountDataModel,
): BankAccountDataModel => {
  return {
    name: apiData.merchantName,
    iban: apiData.bankAccountIBAN,
    associatedPhoneNumbers: apiData.allowedPhonesForChange.map(phone => ({
      id: phone.id,
      maskedPhoneNumber: phone.maskedPhoneNumber,
    })),
  }
}

type ApiSignStatus = {
  data: {
    hasEnded: boolean
    hasEndedSuccesfully: boolean
  }
  meta: MetaModel
}

const ComputeSignStatus = (response: ApiSignStatus): HttpModel<LogaltyResponseStatus> => {
  if (!response.data) {
    return {
      data: LogaltyResponseStatus.Pending,
      meta: response.meta,
    }
  }

  if (!response.data.hasEnded) {
    return {
      data: LogaltyResponseStatus.Pending,
      meta: response.meta,
    }
  } else if (response.data.hasEnded && !response.data.hasEndedSuccesfully) {
    return {
      data: LogaltyResponseStatus.Error,
      meta: response.meta,
    }
  } else if (response.data.hasEnded && response.data.hasEndedSuccesfully) {
    return {
      data: LogaltyResponseStatus.Success,
      meta: response.meta,
    }
  }

  return {
    data: LogaltyResponseStatus.Pending,
    meta: response.meta,
  }
}

class ApiBankAccountRepository implements BankAccountRepository {
  async GetBankAccountData(
    merchantTargetId: number,
  ): Promise<HttpModel<BankAccountDataModel>> {
    const response = await axiosFetch(
      getBankAccountData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: {} as BankAccountDataModel,
        meta: response.meta,
      }
    }

    return {
      data: BankDataFromApiToDomain(response.data),
      meta: response.meta,
    }
  }

  async GetSepaDocument(data: BankAccountChangeForm): Promise<HttpModel<SepaModel>> {
    const response = await axiosFetch(getSepaDocument, HttpMethod.POST, {
      ...data,
      newIban: data.newIban.replace(/\s/g, ''),
      signerDocumentType: Number(data.signerDocumentType),
    })

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async AcceptSepaDocument(
    data: BankAccountConfirmationForm,
  ): Promise<HttpModel<LogaltyModel>> {
    const response = await axiosFetch(acceptSepaDocument, HttpMethod.POST, {
      operationId: data.operationId,
      merchantTargetId: data.merchantTargetId,
    })

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetSignStatus(
    data: BankAccountConfirmationForm,
  ): Promise<HttpModel<LogaltyResponseStatus>> {
    const response = await axiosFetch(getSignStatus, HttpMethod.POST, {
      operationId: data.operationId,
      merchantTargetId: data.merchantTargetId,
    })

    return ComputeSignStatus(response)
  }
}
